import { sanitizePayload } from "@/utils/sanitizePayload";
import type { HttpErrorInfo } from "./types";

/**
 * Server-side error logger for React Server Components.
 * The browser Datadog SDK cannot run on the server; structured logs are emitted for infra ingestion.
 */
export const logServerError = (error: Error, context?: Record<string, unknown>): void => {
  const payload = {
    level: "error",
    message: error.message,
    error: {
      message: error.message,
      stack: error.stack,
      name: error.name,
    },
    context: sanitizePayload(context),
  };

  if (process.env.NEXT_PUBLIC_ENV !== "prod") {
    console.error("[App Error]", error, context);
    return;
  }

  console.error(JSON.stringify(payload));
};

/**
 * Server-side HTTP error logger for axios interceptors in RSC / API routes.
 */
export const logServerHttpError = (errorInfo: HttpErrorInfo): void => {
  const {
    message,
    statusCode,
    endpoint,
    method = "UNKNOWN",
    payload,
    response,
    timestamp = new Date().toISOString(),
    userContext,
  } = errorInfo;

  const structuredPayload = {
    level: "error",
    message,
    http: {
      status_code: statusCode,
      url: endpoint,
      method,
    },
    error: {
      message,
      timestamp,
    },
    request: {
      payload: sanitizePayload(payload),
    },
    response: {
      data: sanitizePayload(response),
    },
    user: sanitizePayload(userContext),
  };

  if (process.env.NEXT_PUBLIC_ENV !== "prod") {
    console.error("[HTTP Error]", errorInfo);
    return;
  }

  console.error(JSON.stringify(structuredPayload));
};
